import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";

const inputClass =
  "w-full bg-transparent border-b-2 border-white/20 focus:border-[#F5B301] outline-none py-4 text-lg text-[#f5f5f0] placeholder:text-white/30 transition-colors duration-300";

export default function AdminPage() {
  const [user, setUser] = useState(undefined);
  const [enquiries, setEnquiries] = useState([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const loadEnquiries = useCallback(async () => {
    try {
      const { data } = await api.get("/enquiries");
      setEnquiries(data);
    } catch (err) {
      if (err?.response?.status === 401) setUser(null);
    }
  }, []);

  useEffect(() => {
    api.get("/auth/me").then((res) => setUser(res.data)).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) loadEnquiries();
  }, [user, loadEnquiries]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  const setStatus = async (id, status) => {
    await api.patch(`/enquiries/${id}/status`, { status }).catch(() => {});
    loadEnquiries();
  };

  if (user === undefined) {
    return <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f0] flex items-center justify-center font-cabinet font-black uppercase tracking-tighter text-2xl" data-testid="admin-loading">Loading…</div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6">
        <div className="w-full max-w-md border border-white/20 bg-[#111113] p-10 shadow-[8px_8px_0_#F5B301]" data-testid="admin-login-card">
          <Link to="/" className="text-xs font-bold uppercase tracking-widest text-white/40 hover:text-[#F5B301] transition-colors duration-300">← Back to site</Link>
          <h1 className="font-cabinet font-black uppercase tracking-tighter text-4xl mt-6 text-[#f5f5f0]">
            Staff <span className="text-[#F5B301]">Login</span>
          </h1>
          <form onSubmit={login} className="space-y-8 mt-10" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-email" className="text-xs font-bold uppercase tracking-widest text-white/50">Email</label>
              <input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="reallifelearners@yahoo.com" className={inputClass} />
            </div>
            <div>
              <label htmlFor="admin-password" className="text-xs font-bold uppercase tracking-widest text-white/50">Password</label>
              <input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputClass} />
            </div>
            {error && <p className="text-sm font-semibold text-[#F5B301]" data-testid="admin-login-error">{error}</p>}
            <button type="submit" data-testid="admin-login-button" disabled={loading} className="w-full bg-[#F5B301] text-[#0a0a0a] font-bold uppercase tracking-wide text-sm px-8 py-4 hover:bg-[#f5f5f0] transition-colors duration-300 disabled:opacity-60">
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f0] px-6 md:px-12 py-12" data-testid="admin-dashboard">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-6 border-b border-white/10 pb-8">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#F5B301] mb-3">Real Life Learners</p>
            <h1 className="font-cabinet font-black uppercase tracking-tighter text-4xl md:text-6xl leading-none">
              Enquiries <span className="text-outline">({enquiries.length})</span>
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={loadEnquiries} data-testid="admin-refresh-button" className="border-2 border-[#f5f5f0] font-bold uppercase tracking-wide text-xs px-5 py-3 hover:bg-[#f5f5f0] hover:text-[#0a0a0a] transition-colors duration-300">
              Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout-button" className="bg-[#F5B301] text-[#0a0a0a] font-bold uppercase tracking-wide text-xs px-5 py-3 hover:bg-[#f5f5f0] transition-colors duration-300">
              Log Out
            </button>
          </div>
        </div>

        {enquiries.length === 0 ? (
          <p className="text-white/50 text-sm uppercase tracking-widest mt-16" data-testid="admin-empty-state">No enquiries yet. Share your site and they'll appear here.</p>
        ) : (
          <div className="overflow-x-auto mt-10">
            <table className="w-full text-left border-collapse" data-testid="enquiries-table">
              <thead>
                <tr className="border-b-2 border-white/30 text-xs font-bold uppercase tracking-widest text-white/50">
                  <th className="py-4 pr-6">Status</th>
                  <th className="py-4 pr-6">Received</th>
                  <th className="py-4 pr-6">Name</th>
                  <th className="py-4 pr-6">Contact</th>
                  <th className="py-4 pr-6">Lesson Option</th>
                  <th className="py-4 pr-6">Start Date</th>
                  <th className="py-4 pr-6">Message</th>
                  <th className="py-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {enquiries.map((enquiry) => (
                  <tr key={enquiry.id} className="border-b border-white/10 align-top" data-testid={`enquiry-row-${enquiry.id}`}>
                    <td className="py-5 pr-6">
                      <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
                        <span className={`w-2.5 h-2.5 rounded-full ${enquiry.status === "new" ? "bg-[#F5B301]" : "bg-green-500"}`} />
                        {enquiry.status}
                      </span>
                    </td>
                    <td className="py-5 pr-6 text-sm whitespace-nowrap">{new Date(enquiry.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</td>
                    <td className="py-5 pr-6 font-cabinet font-bold whitespace-nowrap">{enquiry.name}</td>
                    <td className="py-5 pr-6 text-sm">
                      <a href={`tel:${enquiry.phone}`} className="block hover:text-[#F5B301] transition-colors duration-300">{enquiry.phone}</a>
                      <a href={`mailto:${enquiry.email}`} className="block text-white/50 hover:text-[#F5B301] transition-colors duration-300">{enquiry.email}</a>
                    </td>
                    <td className="py-5 pr-6 text-sm whitespace-nowrap">{enquiry.package}</td>
                    <td className="py-5 pr-6 text-sm whitespace-nowrap">{enquiry.preferred_date || "—"}</td>
                    <td className="py-5 pr-6 text-sm text-white/60 max-w-[220px]">{enquiry.message || "—"}</td>
                    <td className="py-5">
                      <button
                        onClick={() => setStatus(enquiry.id, enquiry.status === "new" ? "contacted" : "new")}
                        data-testid={`enquiry-status-btn-${enquiry.id}`}
                        className="border border-white/30 text-xs font-bold uppercase tracking-widest px-3 py-2 hover:bg-[#F5B301] hover:text-[#0a0a0a] hover:border-[#F5B301] transition-colors duration-300 whitespace-nowrap"
                      >
                        {enquiry.status === "new" ? "Mark Contacted" : "Mark New"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
